import {
  AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, OnInit, signal, ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  catchError, map, merge, of, Subject, switchMap, combineLatest, debounceTime,
} from 'rxjs';

import { AdminService } from '../../admin/services/admin.service';
import { InventoryAction, inventoryTaskTypes } from '../../models/inventory_action.model';
import { InventoryActionService } from '../../services/inventory-action.service';
import { StatusesService } from '../../services/statuses.service';
import { UserService } from '../../services/user.service';
import { WarehousesService } from '../../services/warehouses.service';
import { ErrorDialogComponent } from '../../shared/dialogs/error-dialog/error-dialog.component';
import { FileUploadDialogComponent } from '../../shared/dialogs/file-upload-dialog/file-upload-dialog.component';
import { toUnderscore } from '../../utils/string-utils';
import { User } from '../../models/user';
import { InventoryActionCreateFormComponent } from './form/create/inventory-action-create-form.component';
import { Warehouse } from '../../models/warehouse.model';
import { Status } from '../../models/status.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StockCreateFormComponent } from './form/create/stock-create-form/stock-create-form.component';
import { ComponentType } from '@angular/cdk/portal';
import { LossCreateFormComponent } from './form/create/loss-create-form/loss-create-form.component';
import { MovementCreateFormComponent } from './form/create/movement-create-form/movement-create-form.component';

@Component({
    selector: 'app-inventory-actions',
    templateUrl: './inventory-actions.component.html',
    styleUrls: ['./inventory-actions.component.scss', '../inventory.component.scss'],
    standalone: false
})
export class InventoryActionsComponent implements OnInit, AfterViewInit, OnDestroy {
  allTypes = inventoryTaskTypes;

  creableTypes = [
    'InventoryAction::Stock',
    'InventoryAction::Consume',
    'InventoryAction::Movement',
    'InventoryAction::Loss',
  ];

  private dataChanged$ = new Subject<boolean>();

  dataSource = new MatTableDataSource<InventoryAction>();

  expandedRow: InventoryAction | null = null;

  displayedColumns: string[] = [
    'type',
    'correlative_id',
    'created_at',
    'estimated_completion_date',
    'owner',
    'total_quantity',
    'status',
    'warehouse_name',
  ];

  warehouses: Warehouse[];

  inventoryActions = [];

  isLoading = signal(true);

  isLoading1 = false;

  unsubscribe$: Subject<boolean> = new Subject();

  pageSizes = [10, 25, 50, 100];

  sort = 'updated_at desc';

  statuses = signal<Status[]>([]);

  totalData = 0;

  page = 0;

  totalPages = 0;

  loadingMore = false;

  users: User[];

  @ViewChild(MatSort) sorter: MatSort;

  private readonly _cdr = inject(ChangeDetectorRef);

  private readonly fb = inject(FormBuilder);

  filters = this.fb.group({
    created_at_start: this.fb.control(''),
    created_at_end: this.fb.control(''),
    query: this.fb.control(''),
    'status_id[]': this.fb.control([]),
    type: this.fb.control(''),
    'owner_id[]': this.fb.control([]),
    'warehouse_ids[]': this.fb.control([]),
  });

  showFilters = signal(false);

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<FileUploadDialogComponent>,
    private matDialog: MatDialog,
    private statusService: StatusesService,
    private userService: UserService,
    private warehousesService: WarehousesService,
    public adminService: AdminService,
    public inventoryActionService: InventoryActionService,
  ) {}

  ngOnInit() {

    
  }

  statusesFor(type: string) {
    return this.statuses().filter((status) => status.type === 'Status::' + type);
  }

  ngAfterViewInit() {
    
    this.addRefreshTableSubscription();

    this.addInitialDataSubscription();

    this.addFilterSubscriptions();
  }

  private addRefreshTableSubscription() {
    this.dataSource.sort = this.sorter;
    merge(
      this.sorter.sortChange.asObservable(),
      this.filters.valueChanges,
      this.dataChanged$, // This is used to refresh the data table when an action is created
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(300),
        switchMap(() => {
          this.isLoading.set(true);
          this.page = 1;
          return this.getTableData$(
            this.page,
            20, // Page size
            this.sort,
            this.filters.getRawValue(),
          ).pipe(catchError(() => of(null)));
        }),
        map((data) => {
          this.isLoading.set(false);
          if (data == null) return [];
          this.totalData = data.total_count;
          this.totalPages = data.total_pages;
          return data.items;
        }),
      )
      .subscribe((data) => {
        this._cdr.markForCheck();
        this.dataSource = new MatTableDataSource(data);
      });
  }

  private addInitialDataSubscription() {
    this.isLoading.set(true);

    combineLatest([
      this.warehousesService.index(),
      this.userService.getUsers(),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          console.error(error);
          return of(null)
        }),
      )
      .subscribe((values) => {
        this.isLoading.set(false);
        if (values[0].length === 0 || values[1].length === 0) return;

        this.warehouses = values[0];
        this.filters.get('warehouse_ids[]').setValue(this.warehouses?.map((w) => w.id) || []);
        this.users = values[1];
        this.filters.get('owner_id[]').setValue(this.users.map((user) => user.id).concat([0]));
        this.filters.get('type').setValue('all');
      });

    
  }

  private addFilterSubscriptions() {
    this.filters.get('type').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      if (type && type !== 'all') {
        this.statusService.statuses('Status::' + type).subscribe((statuses) => {
          console.log(statuses);
          this.statuses.set(statuses);
          this.filters.get('status_id[]').setValue(statuses.map((status) => status.id));
        });
      }
    });
  }

  getTableData$(pageNumber: number, pageSize: number, sort: string, filters) {
    return this.inventoryActionService.index(
      pageNumber,
      pageSize,
      sort,
      filters,
    );
  }
  
  onSortChange({ active, direction }: { active: string; direction: string }) {
    if (!direction) {
      this.sort = 'updated_at desc';
      return;
    }

    this.sort = `${active} ${direction}`;
  }

  createAction(type: string, title?: string) {
    const titleToShow = title || this.titleForType(type);
    switch (type) {
      case 'InventoryAction::Stock':
        this.openCreateForm(StockCreateFormComponent, type, titleToShow);
        break;
      case 'InventoryAction::Loss':
        this.openCreateForm(LossCreateFormComponent, type, titleToShow);
        break;
      case 'InventoryAction::Movement':
        this.openCreateForm(MovementCreateFormComponent, type, titleToShow);
        break;
      default:
        break;
    }

  }

  openCreateForm(component: ComponentType<InventoryActionCreateFormComponent>, type: string, title?: string) {
    this.matDialog
      .open(component, { 
        data: {
          inventoryActionType: type,
          title 
        }, 
        minWidth  : '890px',
      })
      .afterClosed().subscribe(() => {
        this.dataChanged$.next(true);
      });
  }

  openUploadForm(type: string) {
    this.dialogRef = this.matDialog
      .open(FileUploadDialogComponent, { data: { title: 'Importar desde archivo' } });

    this.dialogRef.componentInstance.submitClicked.subscribe((data: string) => {
      const parsedData = data
        .split('\n')
        .map((line) => line.split(','))
        .filter((line) => line.length > 0);

      this.isLoading1 = true;
      this.dialogRef.close();

      this.inventoryActionService.import(type, parsedData)
        .subscribe((result: InventoryAction | null) => {
          if (!result) {
            this.matDialog.open(ErrorDialogComponent, { data: { message: 'Error al importar los datos' } });
          }

          this.isLoading1 = false;
          this.dataChanged$.next(true);
        });
    });
  }

  titleForType(type: string) {
    return this.adminService.getText(toUnderscore(type));
  }


  iconForType(type: string): string {
    switch (type) {
      case 'InventoryAction::Stock':
        return 'input';
      case 'InventoryAction::Consume':
        return 'output';
      case 'InventoryAction::Movement':
        return 'swap_horiz';
      case 'InventoryAction::Loss':
        return 'outbox';
      case 'InventoryAction::Pricing':
        return 'request_quote';
      case 'InventoryAction::Return':
        return 'keyboard_return';
      default:
        return 'add'; // Valor por defecto
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  getMore() {
    this._cdr.markForCheck();
    this.loadingMore = true;
    this.page = this.page + 1;
    this.getTableData$(this.page, 20, this.sort, this.filters.getRawValue())
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        this._cdr.markForCheck();
        this.loadingMore = false;
        if (data == null) return;
        this.dataSource.data = this.dataSource.data.concat(data.items);
      });
  }

  toggleRow(row: InventoryAction): void {
    this.expandedRow = this.expandedRow === row ? null : row;
  }

  clearSearch() {
    this._cdr.markForCheck();
    this.filters.get('query').setValue('');
  }

  
}
